import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn, formatDateDisplay, parseDateLocal } from "@/lib/utils";
import { pluralize } from "@/lib/pluralize";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import type { Dependent } from "@shared/schema";
import { canonicalizeEmploymentType } from "@shared/constants/employment";

// Sentinel for employees with no employment type on file.
const UNSPECIFIED_TYPE = "Not specified";

// Status colors match the amber/emerald language used for gap/complete
// indicators elsewhere in the wizard (e.g. Step6RFPParameters).
const STATUS_STYLES: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900",
  cobra: "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900",
};

function StatusBadge({ status }: { status: string | null | undefined }) {
  const key = (status || "").toLowerCase();
  return (
    <Badge variant="outline" className={cn("capitalize font-normal", STATUS_STYLES[key])}>
      {status || "Unknown"}
    </Badge>
  );
}

// Group on the canonical label so legacy spellings ("Full-time", "full-time",
// "Full Time") land in one chip instead of four.
function employmentTypeKey(employmentType: string | null | undefined): string {
  return canonicalizeEmploymentType(employmentType) ?? UNSPECIFIED_TYPE;
}

export function Step3ReviewCensus() {
  const { quoteData, updateQuoteData, relevantEmployees, dependents } =
    useQuoteWizardContext();

  // Filter to only show active or COBRA employees (exclude terminated)
  const eligibleEmployees = relevantEmployees.filter(
    (emp) => emp.status?.toLowerCase() === "active" || emp.status?.toLowerCase() === "cobra"
  );

  // Employment-type filter (full-time / part-time / variable hour / …).
  // All types are included by default; excluding a type hides those employees
  // AND removes them from the quote selection below.
  const employmentTypeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    eligibleEmployees.forEach((emp) => {
      const key = employmentTypeKey(emp.employmentType);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [eligibleEmployees]);

  const [excludedTypes, setExcludedTypes] = useState<Set<string>>(new Set());
  const [nameFilter, setNameFilter] = useState("");

  const visibleEmployees = eligibleEmployees.filter(
    (emp) => !excludedTypes.has(employmentTypeKey(emp.employmentType))
  );
  const visibleEmployeeIds = new Set(visibleEmployees.map((e) => e.id));

  // Search only narrows what's rendered - "select all" and the selection
  // count still act on the full (type-filtered) list, not the search text,
  // so typing a search never silently deselects someone.
  const normalizedNameFilter = nameFilter.trim().toLowerCase();
  const displayedEmployees = normalizedNameFilter
    ? visibleEmployees.filter((emp) =>
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(normalizedNameFilter)
      )
    : visibleEmployees;

  // Prune selections that no longer correspond to a visible, eligible
  // employee. Without this, an employee hidden by status (or by the type
  // filter) silently stays in selectedEmployees and gets quoted anyway.
  useEffect(() => {
    // Employees load async — don't prune against an empty list (would wipe a
    // resumed draft's selections before data arrives).
    if (relevantEmployees.length === 0) return;
    const prunedEmployees = quoteData.selectedEmployees.filter((id) =>
      visibleEmployeeIds.has(id)
    );
    // Dependents also load async — only prune against a loaded list.
    const prunedDependents =
      dependents.length === 0
        ? quoteData.selectedDependents
        : quoteData.selectedDependents.filter((depId) => {
            const dep = dependents.find((d) => d.id === depId);
            return dep ? visibleEmployeeIds.has(dep.employeeId) : false;
          });
    if (
      prunedEmployees.length !== quoteData.selectedEmployees.length ||
      prunedDependents.length !== quoteData.selectedDependents.length
    ) {
      updateQuoteData({
        selectedEmployees: prunedEmployees,
        selectedDependents: prunedDependents,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteData.selectedEmployees, quoteData.selectedDependents, excludedTypes, relevantEmployees, dependents]);

  // Count only selections that are visible
  const selectedEligibleCount = quoteData.selectedEmployees.filter(id => visibleEmployeeIds.has(id)).length;

  // Selected employees missing data the rating engine needs: DOB drives
  // age-based rates, ZIP drives plan availability.
  const selectedWithMissingData = visibleEmployees.filter(
    (emp) =>
      quoteData.selectedEmployees.includes(emp.id) &&
      (!emp.dateOfBirth || !emp.zipCode)
  );

  const getEmployeeDependents = (employeeId: number) => {
    return dependents
      .filter((dep) => dep.employeeId === employeeId)
      .sort((a, b) => {
        // Sort alphabetically by last name, then first name
        const lastA = (a.lastName || '').toLowerCase();
        const lastB = (b.lastName || '').toLowerCase();
        if (lastA !== lastB) return lastA.localeCompare(lastB);
        const firstA = (a.firstName || '').toLowerCase();
        const firstB = (b.firstName || '').toLowerCase();
        return firstA.localeCompare(firstB);
      });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Review Employee Census</h3>
        <p className="text-muted-foreground">
          Select which employees and dependents to include in the quote
        </p>
      </div>

      <div className="space-y-4">
        {/* Name search - filters the table only, doesn't affect selection */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            placeholder="Search employees..."
            className="h-8 pl-8 text-sm"
          />
        </div>

        {/* Employment-type filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">
            Employment type:
          </span>
          {Array.from(employmentTypeCounts.entries()).map(([type, typeCount]) => {
            const excluded = excludedTypes.has(type);
            return (
              <Badge
                key={type}
                variant={excluded ? "outline" : "secondary"}
                className={`cursor-pointer select-none ${
                  excluded
                    ? "text-muted-foreground line-through"
                    : type === UNSPECIFIED_TYPE
                      ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                      : "hover:bg-secondary/80"
                }`}
                title={
                  excluded
                    ? `Click to include ${type} employees in the quote`
                    : `Click to exclude ${type} employees from the quote`
                }
                onClick={() => {
                  setExcludedTypes((prev) => {
                    const next = new Set(prev);
                    if (next.has(type)) next.delete(type);
                    else next.add(type);
                    return next;
                  });
                }}
              >
                {type} ({typeCount})
              </Badge>
            );
          })}
        </div>

        <div className="text-sm">
          <span className="font-medium">
            {selectedEligibleCount} of{" "}
            {visibleEmployees.length} {pluralize(visibleEmployees.length, "employee")} selected
          </span>
          <br />
          <span className="text-muted-foreground">
            {quoteData.selectedDependents.length}{" "}
            {pluralize(quoteData.selectedDependents.length, "dependent")} selected
          </span>
        </div>

        {/* Missing-data warning: these employees can't be rated accurately */}
        {selectedWithMissingData.length > 0 && (
          <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <span className="font-medium">
                {selectedWithMissingData.length} selected employee
                {selectedWithMissingData.length === 1 ? " is" : "s are"} missing
                data needed to quote accurately:
              </span>{" "}
              {selectedWithMissingData
                .map((emp) => {
                  const missing = [
                    !emp.dateOfBirth && "date of birth",
                    !emp.zipCode && "ZIP code",
                  ]
                    .filter(Boolean)
                    .join(" and ");
                  return `${emp.firstName} ${emp.lastName} (${missing})`;
                })
                .join(", ")}
              . Missing date of birth prevents age-based rating; missing ZIP
              code prevents checking plan availability.
            </div>
          </div>
        )}

        <div className="border rounded-lg">
          <div className="grid grid-cols-[24px_1fr_50px_1fr_100px_80px_120px_80px] gap-4 px-4 py-2 bg-muted/50 border-b text-xs font-medium text-muted-foreground">
            <Checkbox
              checked={visibleEmployees.length > 0 && selectedEligibleCount === visibleEmployees.length}
              onCheckedChange={(checked) => {
                if (checked) {
                  const allEmployeeIds = visibleEmployees.map((e) => e.id);
                  const allDependentIds = visibleEmployees.flatMap((emp) =>
                    getEmployeeDependents(emp.id).map((dep) => dep.id)
                  );
                  updateQuoteData({
                    selectedEmployees: allEmployeeIds,
                    selectedDependents: allDependentIds,
                  });
                } else {
                  updateQuoteData({
                    selectedEmployees: [],
                    selectedDependents: [],
                  });
                }
              }}
            />
            <div>Name</div>
            <div>Deps</div>
            <div>Position</div>
            <div>DOB</div>
            <div>Zip</div>
            <div>Annual Salary</div>
            <div>Status</div>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {displayedEmployees.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                No employees match "{nameFilter}".
              </div>
            )}
            <Accordion
              type="multiple"
              className="w-full"
              defaultValue={eligibleEmployees
                .filter(emp => getEmployeeDependents(emp.id).length > 0)
                .map(emp => `employee-${emp.id}`)}
            >
              {displayedEmployees.map((employee) => {
                const isSelected = quoteData.selectedEmployees.includes(
                  employee.id
                );
                const employeeDependents = getEmployeeDependents(employee.id);

                return (
                  <AccordionItem
                    key={employee.id}
                    value={`employee-${employee.id}`}
                    className="border-b last:border-b-0"
                  >
                    <div className="grid grid-cols-[24px_1fr_50px_1fr_100px_80px_120px_80px] gap-4 items-center px-4 py-3 hover:bg-muted/50">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => {
                          const newSelected = checked
                            ? [...quoteData.selectedEmployees, employee.id]
                            : quoteData.selectedEmployees.filter(
                                (id) => id !== employee.id
                              );
                          updateQuoteData({ selectedEmployees: newSelected });
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {employee.firstName} {employee.lastName}
                        </span>
                        <AccordionTrigger className="hover:no-underline py-0 flex-none [&>svg]:h-4 [&>svg]:w-4" />
                      </div>
                      <div className="text-sm text-center">
                        {employeeDependents.length}
                      </div>
                      <div className="text-sm text-muted-foreground truncate">
                        {employee.position || "-"}
                      </div>
                      <div className="text-sm">
                        {employee.dateOfBirth ? (
                          formatDateDisplay(employee.dateOfBirth)
                        ) : (
                          <span
                            className="flex items-center gap-1 text-red-600 font-medium"
                            title="No date of birth on file — age-based rates can't be calculated for this employee."
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Missing
                          </span>
                        )}
                      </div>
                      <div className="text-sm">
                        {employee.zipCode || (
                          <span
                            className="flex items-center gap-1 text-amber-600 font-medium"
                            title="No ZIP code on file — plan availability can't be verified for this employee."
                          >
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Missing
                          </span>
                        )}
                      </div>
                      <div className="text-sm">
                        {employee.compensationAmount
                          ? `$${(employee.compensationType === "hourly"
                              ? Number(employee.compensationAmount) * 2080
                              : Number(employee.compensationAmount)
                            ).toLocaleString()}`
                          : "-"}
                      </div>
                      <div className="text-sm">
                        <StatusBadge status={employee.status} />
                        <div className="mt-1 text-xs text-muted-foreground">
                          {employmentTypeKey(employee.employmentType)}
                        </div>
                      </div>
                    </div>
                    <AccordionContent className="pb-2">
                      {employeeDependents.length > 0 ? (
                        <div className="space-y-1 ml-6 border-l-2 border-muted-foreground/20">
                          {employeeDependents.map((dependent) => {
                            const isDependentSelected =
                              quoteData.selectedDependents.includes(
                                dependent.id
                              );
                            return (
                              <div
                                key={dependent.id}
                                className="grid grid-cols-[24px_1fr_50px_1fr_100px_80px_120px_80px] gap-4 items-center px-4 py-2 bg-muted/30 hover:bg-muted/50"
                              >
                                <Checkbox
                                  checked={isDependentSelected}
                                  onCheckedChange={(checked) => {
                                    const newSelected = checked
                                      ? [
                                          ...quoteData.selectedDependents,
                                          dependent.id,
                                        ]
                                      : quoteData.selectedDependents.filter(
                                          (id) => id !== dependent.id
                                        );
                                    updateQuoteData({
                                      selectedDependents: newSelected,
                                    });
                                  }}
                                  disabled={!dependent.isEligible}
                                />
                                <div className="flex items-center gap-2">
                                  <div className="w-5 h-5 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-medium">
                                      {dependent.relationship === "spouse"
                                        ? "S"
                                        : dependent.relationship === "child"
                                        ? "C"
                                        : "D"}
                                    </span>
                                  </div>
                                  <span className="text-sm font-medium truncate">
                                    {dependent.firstName} {dependent.lastName}
                                  </span>
                                </div>
                                <div></div>
                                <div className="text-sm text-muted-foreground capitalize">
                                  {dependent.relationship}
                                </div>
                                <div className="text-sm">
                                  {dependent.dateOfBirth
                                    ? parseDateLocal(dependent.dateOfBirth)?.toLocaleDateString()
                                    : "-"}
                                </div>
                                <div></div>
                                <div></div>
                                <Badge
                                  variant={
                                    dependent.isEligible
                                      ? "default"
                                      : "secondary"
                                  }
                                  className="justify-self-start"
                                >
                                  {dependent.isEligible
                                    ? "Eligible"
                                    : "Not Eligible"}
                                </Badge>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="px-4 py-2 text-sm text-muted-foreground italic">
                          No dependents on file
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        </div>
      </div>
    </div>
  );
}
